import React, { useEffect, useRef, useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { Info, Plus } from 'lucide-react';

import { OrderSchemaType } from '../order.schema';
import { FormGrid, FormGroup, Label, Select, Separator, InfoLink, HintText } from '../style';
import { SelectFormGroup, InfoLinkText } from './MainInfosSection.style';
import InfoTooltip from '../../../../../components/InfoTooltip';
import QuickContactModal, { CompanyContact } from '../components/QuickContactModal';

import CompanySelect from '../../../FashionOrders/SelectItem/CompanySelect';
import VendorSelect from '../../../FashionOrders/SelectItem/VendorSelect';



interface Company {
  id: number;
  companyName: string;
  cnpj?: string;
  cpf?: string;
  email?: string;
  phone?: string;
  contactName?: string;
  name?: string;
  contacts?: CompanyContact[];
  street?: string;
  number?: string | number;
  neighborhood?: string;
  city?: string;
  state?: string;
  cep?: string | number;
}

interface Seller {
  id: number;
  name: string;
  email?: string;
}

const MainInfosSection: React.FC = () => {
  const { control, watch, setValue, getValues } = useFormContext<OrderSchemaType>();

  const [activeInfo, setActiveInfo] = useState<string | null>(null);
  const [clientContacts, setClientContacts] = useState<CompanyContact[]>([]);
  const [selectedContactId, setSelectedContactId] = useState<number | ''>('');
  const [showContactModal, setShowContactModal] = useState(false);
  const [savedContactNotInList, setSavedContactNotInList] = useState<string | null>(null);
  const contactHydratedRef = useRef(false);

  const applyContactToForm = (contact: CompanyContact | undefined) => {
    setValue('contactId' as any, contact?.id ?? undefined, { shouldDirty: true, shouldValidate: false });
    setValue('contactName' as any, contact?.name ?? undefined, { shouldDirty: true, shouldValidate: false });
    setValue('contactEmail' as any, contact?.email ?? undefined, { shouldDirty: true, shouldValidate: false });
    setValue('contactPhone' as any, contact?.phone ?? undefined, { shouldDirty: true, shouldValidate: false });
  };

  const handleInfoToggle = (type: string, hasDetails: boolean) => {
    if (!hasDetails) return;
    setActiveInfo(activeInfo === type ? null : type);
  };

  const selectedSupplierId = watch('supplierId');
  const selectedClientId = watch('clientId');
  const selectedSellerId = watch('sellerId');

  const selectedSupplier = watch('supplierDetails');
  const selectedClient = watch('clientDetails');
  const selectedSeller = watch('sellerDetails');

  const selectedSupplierName = watch('supplier');
  const selectedClientName = watch('client');
  const selectedSellerName = watch('seller');

  useEffect(() => {
    const details = selectedClient as any;
    const contacts = Array.isArray(details?.contacts)
      ? details.contacts.filter((c: any) => c && c.id != null)
      : [];
    setClientContacts(contacts);

    if (!selectedClientId) return;

    if (!contactHydratedRef.current) {
      contactHydratedRef.current = true;
      const savedContactId = getValues('contactId' as any) as number | undefined;
      const savedContactName = getValues('contactName' as any) as string | undefined;
      if (savedContactId && contacts.some((c: any) => Number(c.id) === Number(savedContactId))) {
        setSelectedContactId(Number(savedContactId));
        setSavedContactNotInList(null);
      } else {
        setSelectedContactId('');
        setSavedContactNotInList(savedContactId ? (savedContactName || null) : null);
      }
      return;
    }

    setSelectedContactId((prev) => {
      const stillValid = prev !== '' && contacts.some((c: any) => Number(c.id) === Number(prev));
      if (!stillValid) {
        applyContactToForm(undefined);
        setSavedContactNotInList(null);
      }
      return stillValid ? prev : '';
    });
  }, [selectedClientId, selectedClient]);



  const buildAddressString = (entity: any): string => {
    const safe = (v: any): string => {
      if (!v && v !== 0) return '';
      if (typeof v === 'string') return v;
      if (typeof v === 'number') return String(v);
      if (typeof v === 'object' && v !== null) return v.name || v.label || '';
      return '';
    };
    const street = safe(entity.street);
    const number = safe(entity.number);
    const neighborhood = safe(entity.neighborhood);
    const city = safe(entity.city);
    const state = safe(entity.state);
    const rawCep = entity.cep || '';
    const cepDigits = String(rawCep).replace(/\D/g, '');
    const cepPadded = cepDigits ? cepDigits.padStart(8, '0') : '';
    const cepFormatted = cepPadded ? `${cepPadded.slice(0, 5)}-${cepPadded.slice(5)}` : '';
    const parts: string[] = [];
    if (street) parts.push(`${street}${number ? `, ${number}` : ''}`);
    if (neighborhood) parts.push(neighborhood);
    if (city) parts.push(city);
    if (state) parts.push(state);
    const main = parts.join(' - ');
    const result = cepFormatted ? `${main}${main ? ' - ' : ''}${cepFormatted}` : main;
    return result.trim();
  };

  const getSupplierDetailsForTooltip = (supplier: any): Record<string, string> => {
    const details: Record<string, string> = {};
    if (supplier) {
      details['Razão Social'] = String(supplier.companyName || supplier.name || 'Não informado');
      if (supplier.isMain) {
        const tipo = supplier.tipoEstabelecimento || (supplier.cnpj ? (supplier.cnpj.replace(/\D/g, '').substring(8, 12) === '0001' ? 'Matriz' : 'Filial') : '');
        if (tipo) details['Tipo'] = tipo;
      }
      details['CNPJ/CPF'] = String(supplier.cnpj || supplier.cpf || 'Não informado');
      details['Nome do Contato'] = String(supplier.contactName || supplier.contato || (supplier.contacts?.[0]?.name) || 'Não informado');
      details['Email'] = String(supplier.email || (supplier.contacts?.[0]?.email) || 'Não informado');
      details['Telefone'] = String(supplier.phone || supplier.telefone || (supplier.contacts?.[0]?.phoneNumber) || 'Não informado');
      const address = buildAddressString(supplier);
      if (address) details['Endereço'] = address;
    }
    return details;
  };

  const getClientDetailsForTooltip = (client: any): Record<string, string> => {
    const details: Record<string, string> = {};
    if (client) {
      details['Razão Social'] = String(client.companyName || client.name || 'Não informado');
      if (client.isMain) {
        const tipo = client.tipoEstabelecimento || (client.cnpj ? (client.cnpj.replace(/\D/g, '').substring(8, 12) === '0001' ? 'Matriz' : 'Filial') : '');
        if (tipo) details['Tipo'] = tipo;
      }
      details['CNPJ/CPF'] = String(client.cnpj || client.cpf || 'Não informado');
      details['Nome do Contato'] = String(client.contactName || client.contato || (client.contacts?.[0]?.name) || 'Não informado');
      details['Email'] = String(client.email || (client.contacts?.[0]?.email) || 'Não informado');
      details['Telefone'] = String(client.phone || client.telefone || (client.contacts?.[0]?.phoneNumber) || 'Não informado');
      const address = buildAddressString(client);
      if (address) details['Endereço'] = address;
    }
    return details;
  };

  const getSellerDetailsForTooltip = (seller: any): Record<string, string> => {
    const details: Record<string, string> = {};
    if (seller) {
      details['Nome'] = String(seller.name || 'Não informado');
      details['Email'] = String(seller.email || 'Não informado');
    }
    return details;
  };

  const API_URL_COMPANIES = `${process.env.REACT_APP_GATEWAY_BASE_URL}/companies/companies`;
  const API_URL_SELLERS = `${process.env.REACT_APP_GATEWAY_BASE_URL}/steel-orders/select-in-order/sellers`;

  return (
    <>
      <FormGrid>
        <SelectFormGroup>
          <Controller
            name="supplierId"
            control={control}
            render={({ field }) => (
              <CompanySelect
                label="Meu cnpj"
                apiUrl={`${process.env.REACT_APP_GATEWAY_BASE_URL}/companies/companies`}
                displayField="companyName"
                queryParams={{ isMain: 'true' }}
                value={{ id: Number(field.value || 0), companyName: watch('supplier') || '' }}
                onChange={(v: any) => {
                  const supplierId = v.id || undefined;
                  field.onChange(supplierId);
                  setValue('supplier', v.companyName || '');
                  setValue('supplierId', supplierId);
                  setActiveInfo(null);
                }}
              />
            )}
          />
          <InfoLink
            disabled={!selectedSupplierId}
            onClick={() => handleInfoToggle('supplier', !!selectedSupplierId)}
          >
            <Info size={12} />
            <InfoLinkText>Mais informações</InfoLinkText>
          </InfoLink>
          {activeInfo === 'supplier' && selectedSupplier && (
            <InfoTooltip details={getSupplierDetailsForTooltip(selectedSupplier)} />
          )}
        </SelectFormGroup>

        <SelectFormGroup>
          <Controller
            name="sellerId"
            control={control}
            render={({ field }) => (
              <VendorSelect<Seller>
                label="Vendedor"
                apiUrl={API_URL_SELLERS}
                displayField="name"
                value={(watch('sellerDetails') as Seller) || {
                  id: watch('sellerId') || 0,
                  name: watch('seller') || ''
                }}
                onChange={(newSeller) => {
                  field.onChange(newSeller.id || undefined);
                  setValue('seller', newSeller.name || '');
                  setValue('sellerId', newSeller.id || undefined, { shouldDirty: true });
                  setValue('sellerDetails', newSeller, { shouldDirty: true });
                  setActiveInfo(null);
                }}
              />
            )}
          />
          <InfoLink
            disabled={!selectedSellerId}
            onClick={() => handleInfoToggle('seller', !!selectedSellerId)}
          >
            <Info size={12} />
            <InfoLinkText>Mais informações</InfoLinkText>
          </InfoLink>
          {activeInfo === 'seller' && selectedSeller && (
            <InfoTooltip details={getSellerDetailsForTooltip(selectedSeller)} />
          )}
        </SelectFormGroup>

        <SelectFormGroup>
          <Controller
            name="clientId"
            control={control}
            render={({ field }) => (
              <CompanySelect<Company>
                label="Cliente"
                apiUrl={API_URL_COMPANIES}
                displayField="companyName"
                queryParams={{ companyType: 'Cliente' }}
                value={(watch('clientDetails') as Company) || {
                  id: watch('clientId') || 0,
                  companyName: watch('client') || ''
                }}
                onChange={(newClient) => {
                  field.onChange(newClient.id || undefined);
                  setValue('client', newClient.companyName || '');
                  setValue('clientId', newClient.id || undefined, { shouldDirty: true });
                  setValue('clientDetails', newClient, { shouldDirty: true });
                  setActiveInfo(null);
                }}
              />
            )}
          />
          <InfoLink
            disabled={!selectedClientId}
            onClick={() => handleInfoToggle('client', !!selectedClientId)}
          >
            <Info size={12} />
            <InfoLinkText>Mais informações</InfoLinkText>
          </InfoLink>
          {activeInfo === 'client' && selectedClient && (
            <InfoTooltip details={getClientDetailsForTooltip(selectedClient)} />
          )}
        </SelectFormGroup>
      </FormGrid>
      <FormGrid>
        <FormGroup />
        <FormGroup />
        <FormGroup>
          <Label htmlFor="clientContactId">Contato</Label>
          <Select
            id="clientContactId"
            value={selectedContactId}
            disabled={!selectedClientId}
            onChange={(e) => {
              const id = e.target.value ? Number(e.target.value) : '';
              setSelectedContactId(id);
              setSavedContactNotInList(null);
              const contact = id ? clientContacts.find((c) => Number(c.id) === id) : undefined;
              applyContactToForm(contact);
            }}
          >
            <option value="">Selecione</option>
            {clientContacts.map((contact) => (
              <option key={contact.id} value={contact.id}>{contact.name}</option>
            ))}
          </Select>
          {savedContactNotInList && (
            <HintText>Contato salvo no pedido: {savedContactNotInList} (não está mais na lista do cliente)</HintText>
          )}
          <InfoLink
            disabled={!selectedClientId}
            onClick={() => selectedClientId && setShowContactModal(true)}
          >
            <Plus size={12} />
            <InfoLinkText>Novo contato</InfoLinkText>
          </InfoLink>
        </FormGroup>
      </FormGrid>
      {showContactModal && !!selectedClientId && (
        <QuickContactModal
          isOpen={showContactModal}
          onClose={() => setShowContactModal(false)}
          companyId={Number(selectedClientId)}
          companyName={selectedClientName || String((selectedClient as any)?.companyName || 'cliente selecionado')}
          onSaved={(contact) => {
            setClientContacts((prev) => [...prev, contact]);
            setSelectedContactId(contact.id);
            setSavedContactNotInList(null);
            applyContactToForm(contact);
            if (selectedClient) {
              const details = selectedClient as Record<string, any>;
              const contacts = Array.isArray(details.contacts) ? [...details.contacts, contact] : [contact];
              setValue('clientDetails', { ...details, contacts }, { shouldDirty: false, shouldValidate: false });
            }
          }}
        />
      )}
      <Separator />
    </>
  );
};

export default MainInfosSection;